import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LogOut, CalendarDays, MapPin, ShieldCheck, Flame } from "lucide-react";
import { PageHero, Reveal, SectionHead, Tag } from "@/components/Shared";
import { api, useCollection, fmtDate, IMG } from "@/lib/api";

export default function Members() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const navigate = useNavigate();
  const events = useCollection("events");

  useEffect(() => {
    api
      .get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  if (!user) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 size={26} className="animate-spin text-silver-muted" />
      </main>
    );
  }

  const upcoming = (events.items || []).filter((e) => e.status === "upcoming").slice(0, 4);
  const since = user.created_at ? fmtDate(user.created_at) : "—";

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/");
  };

  return (
    <main>
      <PageHero eyebrow="Members Area" title="Welcome back," italic={user.name?.split(" ")[0] || "Member"} image={IMG.forestMist}>
        <p className="text-sm leading-relaxed text-silver-dim sm:text-base">
          This is your corner of the club — your profile, and what's coming up next.
        </p>
      </PageHero>

      <section className="mx-auto max-w-7xl px-6 py-20" data-testid="members-section">
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <div className="card-line corner-frame p-10" data-testid="member-profile-card">
              <div className="flex items-center gap-5">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="h-20 w-20 rounded-full border border-silver/25 object-cover" data-testid="member-avatar" />
                ) : (
                  <span className="flex h-20 w-20 items-center justify-center rounded-full border border-silver/25 bg-navy-light font-display text-3xl metallic" data-testid="member-avatar">
                    {(user.name || "M").slice(0, 1).toUpperCase()}
                  </span>
                )}
                <div>
                  <p className="font-display text-3xl font-semibold text-silver">{user.name}</p>
                  <p className="mt-1 text-sm text-silver-muted">{user.email}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Tag tone="ember">{user.role === "admin" ? "Admin" : "Member"}</Tag>
                    <Tag>Since {since}</Tag>
                  </div>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {user.role === "admin" && (
                  <Link to="/admin" className="btn-ember !px-5 !py-2.5" data-testid="member-admin-btn">
                    <ShieldCheck size={14} /> <span>Open Admin</span>
                  </Link>
                )}
                <button onClick={logout} className="btn-silver !px-5 !py-2.5" data-testid="member-logout-btn">
                  <span>Log Out</span> <LogOut size={13} />
                </button>
              </div>
            </div>
          </Reveal>

          <div>
            <SectionHead eyebrow="Coming Up" title="Your next" italic="club moments" />
            <div className="mt-8 space-y-4" data-testid="member-events-list">
              {upcoming.map((e, i) => (
                <Reveal key={e.id} delay={i * 0.06}>
                  <Link to="/calendar" className="card-line group flex items-center gap-5 p-5" data-testid={`member-event-${e.id}`}>
                    <Flame size={18} className="shrink-0 text-ember" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-xl text-silver group-hover:text-white">{e.title}</p>
                      <p className="mt-1 flex items-center gap-3 text-xs text-silver-muted">
                        <span className="inline-flex items-center gap-1.5"><CalendarDays size={12} /> {fmtDate(e.date)}</span>
                        <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {e.venue || "TBA"}</span>
                      </p>
                    </div>
                    <Tag>{e.category}</Tag>
                  </Link>
                </Reveal>
              ))}
              {upcoming.length === 0 && (
                <p className="text-sm italic text-silver-muted">New events will be announced soon — keep an eye on the calendar.</p>
              )}
            </div>
            <Reveal delay={0.2}>
              <div className="corner-frame mt-8 border border-silver/20 bg-navy/50 p-7">
                <p className="font-display text-xl italic text-silver">Questions about the club?</p>
                <p className="mt-2 text-sm text-silver-muted">
                  Tap the ember chat bubble in the bottom-right corner — Ember, our AI assistant, knows the club inside out.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
